import { Fragment } from "react";
import {
    Avatar,
    type AvatarShape,
    type AvatarSize,
    type AvatarTone,
    type AvatarVariant,
} from "@design-system/Avatar/Avatar";
import { VStack } from "@design-system/VStack/VStack";
import { KickLogo } from "@design-system/KickLogo/KickLogo";
import {
    avatarContentLabels,
    avatarContents,
    avatarPaintProps,
    avatarShapes,
    avatarSizePixels,
    avatarSizes,
    avatarTones,
    avatarVariants,
    brokenPhotoSrc,
    firmLogoSrc,
    pastelColors,
    personPhotoSrc,
    type AvatarContent,
} from "./AvatarPlayground.lib";
import styles from "./AvatarPlayground.module.scss";

/** A matrix cell the Avatar types rule out, e.g. a photo on a diamond. */
const NotApplicable = ({ reason }: { reason: string }) => (
    <span className={styles.notApplicable} title={reason}>
        n/a
    </span>
);

/**
 * One Avatar for a sandbox or matrix cell. Circle photos show a person;
 * square photos show a firm or entity logo, as in Figma.
 */
export const AvatarSample = ({
    content,
    size,
    shape,
    variant,
    tone,
    color,
}: {
    content: AvatarContent;
    size: AvatarSize;
    shape: AvatarShape;
    variant: AvatarVariant;
    tone: AvatarTone;
    color?: string;
}) => {
    const paint = avatarPaintProps({ variant, tone, color });

    switch (content) {
        case "photo":
            if (shape === "diamond") {
                return <NotApplicable reason="Diamonds never show a photo." />;
            }
            return shape === "circle" ? (
                <Avatar
                    name="Conrad Wolff"
                    src={personPhotoSrc}
                    size={size}
                    shape={shape}
                    {...paint}
                />
            ) : (
                <Avatar
                    name="Zhong & Sanches"
                    src={firmLogoSrc}
                    size={size}
                    shape={shape}
                    {...paint}
                />
            );
        case "initialColor":
            if (tone === "neutral") {
                return (
                    <NotApplicable reason="Neutral avatars take no colour." />
                );
            }
            return (
                <Avatar
                    name="Maya Patel"
                    size={size}
                    shape={shape}
                    variant={variant}
                    color={color ?? pastelColors[0].value}
                />
            );
        case "initial":
            return (
                <Avatar
                    name="Maya Patel"
                    size={size}
                    shape={shape}
                    {...paint}
                />
            );
        case "brokenPhoto":
            if (shape === "diamond") {
                return <NotApplicable reason="Diamonds never show a photo." />;
            }
            return (
                <Avatar
                    name="Ben Carter"
                    src={brokenPhotoSrc}
                    size={size}
                    shape={shape}
                    {...paint}
                />
            );
        case "fallback": {
            if (shape === "diamond") {
                return <NotApplicable reason="Classes have no logo." />;
            }
            return (
                <Avatar
                    name="Kick"
                    size={size}
                    shape={shape}
                    fallback={<KickLogo variant="icon" shape={shape} />}
                    {...paint}
                />
            );
        }
    }
};

const ShapeGrid = ({
    shape,
    variant,
    tone,
}: {
    shape: AvatarShape;
    variant: AvatarVariant;
    tone: AvatarTone;
}) => (
    <VStack gap={8}>
        <h3
            className={styles.stateTitle}
        >{`${shape} · ${variant} · ${tone}`}</h3>
        <div className={styles.grid}>
            <span className={styles.columnHeader}>size</span>
            {avatarContents.map((content) => (
                <span key={content} className={styles.columnHeader}>
                    {avatarContentLabels[content]}
                </span>
            ))}
            {avatarSizes.map((size) => (
                <Fragment key={size}>
                    <span className={styles.rowLabel}>
                        {`${size} · ${avatarSizePixels[size]}px`}
                    </span>
                    {avatarContents.map((content) => (
                        <span key={content} className={styles.cell}>
                            <AvatarSample
                                content={content}
                                size={size}
                                shape={shape}
                                variant={variant}
                                tone={tone}
                            />
                        </span>
                    ))}
                </Fragment>
            ))}
        </div>
    </VStack>
);

/** Every size × shape × variant × tone × content cell of the Figma UserIcon, EntityIcon and ClassIcon, plus the invented lg. */
export const AvatarMatrix = () => (
    <section className={styles.matrix}>
        <VStack gap={4}>
            <h2 className={styles.sectionTitle}>Avatar matrix</h2>
            <span className={styles.sectionDescription}>
                Figma draws xs, sm and md for people and xs for entities and
                classes; the rest is invented. A broken photo falls back to the
                initial. Diamonds never show a photo and neutral avatars take no
                colour: a colorful outline puts its colour on the ring.
            </span>
        </VStack>
        {avatarShapes.map((shape) =>
            avatarVariants.flatMap((variant) =>
                avatarTones.map((tone) => (
                    <ShapeGrid
                        key={`${shape}-${variant}-${tone}`}
                        shape={shape}
                        variant={variant}
                        tone={tone}
                    />
                )),
            ),
        )}
    </section>
);
